/**
 * ResetPasswordPage
 * หน้ารีเซ็ตรหัสผ่าน
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { authApi } from './auth.api'
import { PASSWORD_RULES, getPasswordStrength } from './password.validation'
import { ArrowLeft, Loader2, CheckCircle, Eye, EyeOff, Check, X } from 'lucide-react'

const schema = z
  .object({
    newPassword: z
      .string()
      .min(8)
      .regex(/[A-Z]/)
      .regex(/[a-z]/)
      .regex(/[0-9]/)
      .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/),
    confirmPassword: z.string().min(1),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type FormValues = z.infer<typeof schema>

export function ResetPasswordPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [isLoading, setIsLoading] = useState(false)
  const [isReset, setIsReset] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isThaiLang = i18n.language === 'th'

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const newPassword = watch('newPassword') || ''
  const strength = getPasswordStrength(newPassword)

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    setError(null)
    try {
      await authApi.resetPassword({ token: token ?? '', ...values })
      setIsReset(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  const getStrengthColor = () => {
    if (strength < 40) return 'bg-red-500'
    if (strength < 60) return 'bg-orange-500'
    if (strength < 80) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#0c2d6b] via-[#1a3f7a] to-[#0f3460] px-4">
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-8 shadow-2xl backdrop-blur-xl">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-white">
              Go<span className="text-[#60a5fa]">Audit</span>
            </h1>
          </div>

          {isReset ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white">{t('auth.passwordChanged')}</h2>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('auth.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-white">{t('auth.resetPasswordTitle')}</h2>
                <p className="mt-1 text-sm text-white/50">{t('auth.resetPasswordSubtitle')}</p>
              </div>

              {error && (
                <div className="mb-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <form
                onSubmit={(e) => {
                  void handleSubmit(onSubmit)(e)
                }}
                className="space-y-5"
              >
                {/* New Password */}
                <div className="space-y-2">
                  <label htmlFor="new-pw" className="text-sm font-medium text-white/80">
                    {t('auth.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="new-pw"
                      type={showPassword ? 'text' : 'password'}
                      className={`focus:ring-primary/30 h-11 w-full rounded-lg border bg-white/5 px-4 pr-10 text-sm text-white placeholder-white/30 focus:ring-2 focus:outline-none ${
                        errors.newPassword ? 'border-red-500' : 'border-white/20'
                      }`}
                      {...register('newPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowPassword(!showPassword)
                      }}
                      className="absolute top-1/2 right-3 -translate-y-1/2 text-white/40"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Password Strength & Rules */}
                {newPassword && (
                  <div className="space-y-2">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                      <div
                        className={`h-full transition-all ${getStrengthColor()}`}
                        style={{ width: `${String(strength)}%` }}
                      />
                    </div>
                    <div className="space-y-1">
                      {PASSWORD_RULES.map((rule) => {
                        const passed = rule.test(newPassword)
                        return (
                          <div key={rule.id} className="flex items-center gap-2 text-xs">
                            {passed ? (
                              <Check className="h-3 w-3 text-green-400" />
                            ) : (
                              <X className="h-3 w-3 text-white/30" />
                            )}
                            <span className={passed ? 'text-green-400' : 'text-white/40'}>
                              {isThaiLang ? rule.labelTh : rule.labelEn}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label htmlFor="confirm-pw" className="text-sm font-medium text-white/80">
                    {t('auth.confirmPassword')}
                  </label>
                  <input
                    id="confirm-pw"
                    type="password"
                    className={`focus:ring-primary/30 h-11 w-full rounded-lg border bg-white/5 px-4 text-sm text-white focus:ring-2 focus:outline-none ${
                      errors.confirmPassword ? 'border-red-500' : 'border-white/20'
                    }`}
                    {...register('confirmPassword')}
                  />
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-400">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <Button type="submit" className="h-11 w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.resetPassword')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
